import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import api, { formatApiError } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { CalendarOff, Plus, Check, X, AlertTriangle, Building2, ArrowLeft, Users, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

export default function LeavePage() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [leaves, setLeaves] = useState([]);
  const [branchSummary, setBranchSummary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [rejectDialog, setRejectDialog] = useState({ open: false, id: null });
  const [rejectReason, setRejectReason] = useState('');
  const [filterStatus, setFilterStatus] = useState(searchParams.get('status') || 'all');
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [flatView, setFlatView] = useState(searchParams.get('view') === 'all');
  const [form, setForm] = useState({ start_date: '', end_date: '', reason: '', leave_type: 'leave', is_emergency: false, is_half_day: false, half_day_session: 'morning' });

  const isManager = ['super_admin', 'management', 'branch_manager'].includes(user?.system_role);
  const isFinalApprover = ['super_admin', 'management'].includes(user?.system_role);
  const isSuperAdmin = user?.system_role === 'super_admin';
  // Flat view shows all branches consolidated (bypasses branch-summary gate).
  const showBranchSummary = isFinalApprover && !selectedBranch && !flatView;
  // branch_id → branch_name lookup, used to render a "Branch" column in flat view
  // and to flag leaves whose branch has been deleted (orphan branch_id).
  const branchMap = branchSummary.reduce((acc, b) => { acc[b.branch_id] = b.branch_name; return acc; }, {});

  const fetchLeaves = async () => {
    setLoading(true);
    setLeaves([]);
    try {
      const params = {};
      if (filterStatus && filterStatus !== 'all') params.status = filterStatus;
      if (selectedBranch) params.branch_id = selectedBranch;
      const [lRes, sRes] = await Promise.all([
        api.get('/leaves', { params }),
        isManager ? api.get('/leaves/branch-summary') : Promise.resolve({ data: [] })
      ]);
      setLeaves(lRes.data);
      setBranchSummary(sRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchLeaves(); /* eslint-disable-next-line */ }, [filterStatus, selectedBranch, flatView]);

  // Sync URL params when flatView / filterStatus change, so the view is shareable
  // and persists across refreshes.
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (flatView) next.set('view', 'all'); else next.delete('view');
    if (filterStatus && filterStatus !== 'all') next.set('status', filterStatus);
    else next.delete('status');
    setSearchParams(next, { replace: true });
    /* eslint-disable-next-line */
  }, [flatView, filterStatus]);

  const handleApply = async () => {
    try {
      await api.post('/leaves', form);
      toast.success(form.is_emergency ? 'Emergency leave submitted — Super Admin notified' : 'Leave applied successfully');
      setDialogOpen(false);
      setForm({ start_date: '', end_date: '', reason: '', leave_type: 'leave', is_emergency: false, is_half_day: false, half_day_session: 'morning' });
      fetchLeaves();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const handleApprove = async (id) => {
    try {
      await api.put(`/leaves/${id}/approve`);
      toast.success('Leave approved');
      fetchLeaves();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const handleReject = async () => {
    try {
      await api.put(`/leaves/${rejectDialog.id}/reject`, { reason: rejectReason });
      toast.success('Leave rejected');
      setRejectDialog({ open: false, id: null });
      setRejectReason('');
      fetchLeaves();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const handleDeleteLeave = async (id, emp, status) => {
    if (!window.confirm(`Delete this ${status} leave request for ${emp || 'employee'}? This cannot be undone.`)) return;
    try {
      await api.delete(`/leaves/${id}`);
      toast.success('Leave deleted');
      fetchLeaves();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const statusColor = (s) => {
    if (s === 'approved') return 'bg-emerald-100 text-emerald-700';
    if (s === 'rejected') return 'bg-red-100 text-red-700';
    if (s === 'manager_approved') return 'bg-blue-100 text-blue-700';
    return 'bg-amber-100 text-amber-700';
  };

  const statusLabel = (s) => {
    if (s === 'manager_approved') return 'Manager Approved';
    return s;
  };

  // Branch manager can action 'pending'. Super admin/management can action 'pending' AND 'manager_approved'.
  const canAction = (l) => {
    if (!isManager) return false;
    if (l.status === 'pending') return true;
    if (l.status === 'manager_approved' && isFinalApprover) return true;
    return false;
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="leave-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            {(selectedBranch || flatView) && isFinalApprover && (
              <Button variant="ghost" size="sm" className="h-8 rounded-sm -ml-2"
                onClick={() => { setSelectedBranch(null); setFlatView(false); }}
                data-testid="back-to-branches">
                <ArrowLeft className="h-4 w-4 mr-1" /> Branches
              </Button>
            )}
            <h1 className="font-heading font-black text-neutral-950 text-2xl sm:text-3xl tracking-tight">Leave Management</h1>
          </div>
          <p className="text-neutral-500 text-sm mt-1">
            {showBranchSummary ? 'Branch-wise leave dashboard · tap a branch for requests' : (flatView && isFinalApprover ? 'All branches consolidated view' : (isManager ? 'Manage team leave requests' : 'Apply and track your leaves'))}
          </p>
        </div>
        <div className="flex gap-3 flex-wrap">
          {showBranchSummary && isFinalApprover && (
            <Button variant="outline" size="sm" className="rounded-sm h-9 border-neutral-300"
              onClick={() => setFlatView(true)} data-testid="view-all-branches-flat">
              <Users className="h-4 w-4 mr-2" /> View All Branches (Flat)
            </Button>
          )}
          {!showBranchSummary && (
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-36 rounded-sm" data-testid="leave-filter-status">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="pending,manager_approved">Pending + Mgr Approved</SelectItem>
                <SelectItem value="manager_approved">Manager Approved</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
          )}
          <Button className="bg-red-600 hover:bg-red-700 text-white rounded-sm" data-testid="apply-leave-button"
            onClick={() => setDialogOpen(true)}>
            <Plus className="h-4 w-4 mr-2" /> Apply Leave
          </Button>
        </div>
      </div>

      {showBranchSummary ? (
        <div>
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[1,2,3,4,5,6].map(i => <div key={i} className="h-40 bg-neutral-100 rounded-md animate-pulse" />)}
            </div>
          ) : branchSummary.length === 0 ? (
            <Card className="border border-neutral-200 shadow-sm rounded-md">
              <CardContent className="py-16 text-center text-neutral-500">
                <Building2 className="h-10 w-10 mx-auto mb-3 text-neutral-300" />
                <p className="text-sm">No branch data available.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="leave-branch-summary-grid">
              {branchSummary.map(b => (
                <button
                  key={b.branch_id}
                  onClick={() => setSelectedBranch(b.branch_id)}
                  data-testid={`leave-branch-card-${b.branch_id}`}
                  className="text-left bg-white border border-neutral-200 rounded-md p-5 shadow-sm hover:border-red-400 hover:shadow-md transition-all group"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-bold uppercase tracking-wider text-neutral-500">Branch</p>
                      <h3 className="font-heading font-bold text-lg text-neutral-950 mt-0.5 group-hover:text-red-600 transition-colors truncate">{b.branch_name}</h3>
                    </div>
                    {b.pending_leaves > 0 && (
                      <Badge className="text-[10px] bg-amber-100 text-amber-800 border-amber-200 ml-2">{b.pending_leaves} pending</Badge>
                    )}
                  </div>
                  <div className="grid grid-cols-3 gap-2 mb-3">
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-wider text-neutral-500">Staff</p>
                      <p className="text-xl font-heading font-black text-neutral-900 flex items-center gap-1"><Users className="h-4 w-4 text-neutral-400" />{b.total_staff}</p>
                    </div>
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-wider text-neutral-500">Absent Today</p>
                      <p className={`text-xl font-heading font-black ${b.todays_absent_count > 0 ? 'text-red-600' : 'text-emerald-600'}`}>{b.todays_absent_count}</p>
                    </div>
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-wider text-neutral-500">Half Day</p>
                      <p className="text-xl font-heading font-black text-amber-600">{b.half_day_today_count ?? 0}</p>
                    </div>
                  </div>
                  {b.todays_absentees?.length > 0 && (
                    <div className="mt-2">
                      <p className="text-[10px] font-bold uppercase tracking-wider text-red-600 mb-1">Today's Absentees</p>
                      <p className="text-xs text-neutral-700 line-clamp-2">{b.todays_absentees.map(a => a.name).join(', ')}</p>
                    </div>
                  )}
                  {b.upcoming_leaves?.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-neutral-100">
                      <p className="text-[10px] font-bold uppercase tracking-wider text-neutral-500 mb-1">Upcoming Leaves ({b.upcoming_leaves.length})</p>
                      <div className="text-xs text-neutral-600 space-y-0.5">
                        {b.upcoming_leaves.slice(0, 3).map(l => (
                          <div key={l.id} className="truncate">• {l.employee_name} — {l.start_date}</div>
                        ))}
                      </div>
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      ) : (
      <Card className="border border-neutral-200 shadow-sm rounded-md overflow-hidden">
        {flatView && isFinalApprover && (
          <div className="px-4 py-2 bg-blue-50/60 border-b border-blue-100 text-xs text-blue-800 flex items-center gap-2 flex-wrap">
            <Users className="h-3.5 w-3.5" />
            <span>Flat view — showing leaves from all branches (including leaves with deleted / missing branch assignment).</span>
          </div>
        )}
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-neutral-50">
                {isManager && <TableHead className="text-xs font-bold uppercase tracking-wider text-neutral-500">Employee</TableHead>}
                {flatView && isFinalApprover && <TableHead className="text-xs font-bold uppercase tracking-wider text-neutral-500">Branch</TableHead>}
                <TableHead className="text-xs font-bold uppercase tracking-wider text-neutral-500">Type</TableHead>
                <TableHead className="text-xs font-bold uppercase tracking-wider text-neutral-500">From</TableHead>
                <TableHead className="text-xs font-bold uppercase tracking-wider text-neutral-500">To</TableHead>
                <TableHead className="text-xs font-bold uppercase tracking-wider text-neutral-500 hidden sm:table-cell">Reason</TableHead>
                <TableHead className="text-xs font-bold uppercase tracking-wider text-neutral-500">Status</TableHead>
                {isManager && <TableHead className="text-xs font-bold uppercase tracking-wider text-neutral-500 text-right">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                Array.from({ length: 3 }).map((_, i) => (
                  <TableRow key={i}><TableCell colSpan={8}><div className="h-8 bg-neutral-100 rounded animate-pulse" /></TableCell></TableRow>
                ))
              ) : leaves.length === 0 ? (
                <TableRow><TableCell colSpan={8} className="text-center py-12 text-neutral-500">
                  <CalendarOff className="h-8 w-8 mx-auto mb-2 text-neutral-300" /> No leave requests found
                </TableCell></TableRow>
              ) : leaves.map(l => {
                const bName = branchMap[l.branch_id];
                const orphanBranch = flatView && isFinalApprover && (!l.branch_id || !bName);
                return (
                <TableRow key={l.id} className={`hover:bg-neutral-50 ${orphanBranch ? 'bg-amber-50/40' : ''}`}>
                  {isManager && <TableCell className="text-sm font-medium text-neutral-800">
                    {l.employee_name}
                    {l.is_emergency && (
                      <Badge className="ml-2 text-[10px] bg-red-100 text-red-700 border-red-200">
                        <AlertTriangle className="h-2.5 w-2.5 mr-1" /> EMERGENCY
                      </Badge>
                    )}
                  </TableCell>}
                  {flatView && isFinalApprover && (
                    <TableCell className="text-xs">
                      {orphanBranch ? (
                        <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-[10px]">
                          <AlertTriangle className="h-2.5 w-2.5 mr-1" /> Unassigned
                        </Badge>
                      ) : (
                        <span className="text-neutral-700">{bName}</span>
                      )}
                    </TableCell>
                  )}
                  <TableCell className="text-sm text-neutral-600">
                    {l.is_half_day && (
                      <Badge className="text-[10px] bg-amber-100 text-amber-700">
                        ½ DAY{l.half_day_session ? ` · ${l.half_day_session}` : ''}
                      </Badge>
                    )}
                    {!isManager && l.is_emergency && (
                      <Badge className="ml-2 text-[10px] bg-red-100 text-red-700">EMERGENCY</Badge>
                    )}
                    {!l.is_half_day && !(!isManager && l.is_emergency) && (
                      <span className="text-neutral-500 text-xs">Leave</span>
                    )}
                  </TableCell>
                  <TableCell className="text-sm text-neutral-800">{l.start_date}</TableCell>
                  <TableCell className="text-sm text-neutral-800">{l.end_date}</TableCell>
                  <TableCell className="text-sm text-neutral-600 hidden sm:table-cell max-w-[200px] truncate">{l.reason}</TableCell>
                  <TableCell>
                    <Badge className={`text-xs capitalize ${statusColor(l.status)}`}>{statusLabel(l.status)}</Badge>
                    {l.manager_approved_by_name && l.status === 'manager_approved' && (
                      <p className="text-[10px] text-blue-600 mt-1">by {l.manager_approved_by_name} · awaiting admin</p>
                    )}
                    {l.rejection_reason && <p className="text-xs text-red-500 mt-1">{l.rejection_reason}</p>}
                  </TableCell>
                  {isManager && (
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        {canAction(l) && (
                          <>
                            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleApprove(l.id)}
                              data-testid={`approve-leave-${l.id}`}>
                              <Check className="h-4 w-4 text-emerald-600" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-8 w-8"
                              onClick={() => setRejectDialog({ open: true, id: l.id })}
                              data-testid={`reject-leave-${l.id}`}>
                              <X className="h-4 w-4 text-red-500" />
                            </Button>
                          </>
                        )}
                        {isSuperAdmin && (
                          <Button variant="ghost" size="icon" className="h-8 w-8"
                            onClick={() => handleDeleteLeave(l.id, l.employee_name, l.status)}
                            data-testid={`delete-leave-${l.id}`}>
                            <Trash2 className="h-4 w-4 text-red-600" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  )}
                </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </Card>
      )}

      {/* Apply Leave Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md" data-testid="apply-leave-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold">Apply for Leave</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {/* Half-day toggle — collapses the end-date field and adds a session picker. */}
            <label
              className={`flex items-start gap-2 p-3 border rounded-sm cursor-pointer transition-colors ${form.is_half_day ? 'border-amber-300 bg-amber-50/50' : 'border-neutral-200 hover:border-neutral-300'}`}
              data-testid="half-day-toggle-label"
            >
              <input
                type="checkbox"
                checked={form.is_half_day}
                onChange={e => setForm(p => ({
                  ...p,
                  is_half_day: e.target.checked,
                  // when toggling on, force start_date = end_date
                  end_date: e.target.checked && p.start_date ? p.start_date : p.end_date,
                }))}
                className="mt-0.5"
                data-testid="leave-half-day-checkbox"
              />
              <div>
                <span className="text-sm font-semibold text-amber-700">Apply as Half Day</span>
                <p className="text-[11px] text-neutral-600 mt-0.5">Single date. Choose morning or afternoon below.</p>
              </div>
            </label>
            {form.is_half_day && (
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">Session</Label>
                <Select value={form.half_day_session} onValueChange={v => setForm(p => ({ ...p, half_day_session: v }))}>
                  <SelectTrigger className="rounded-sm" data-testid="half-day-session-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="morning">Morning (1st half)</SelectItem>
                    <SelectItem value="afternoon">Afternoon (2nd half)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">{form.is_half_day ? 'Date' : 'From'}</Label>
                <Input type="date" value={form.start_date}
                  onChange={e => setForm(p => ({
                    ...p,
                    start_date: e.target.value,
                    // half-day: lock end_date to match start_date
                    end_date: p.is_half_day ? e.target.value : p.end_date,
                  }))}
                  className="rounded-sm" data-testid="leave-start-date" />
              </div>
              <div className="space-y-1.5">
                <Label className={`text-sm font-medium ${form.is_half_day ? 'text-neutral-400' : ''}`}>To</Label>
                <Input type="date" value={form.end_date}
                  onChange={e => setForm(p => ({ ...p, end_date: e.target.value }))}
                  disabled={form.is_half_day}
                  className="rounded-sm disabled:bg-neutral-100 disabled:cursor-not-allowed"
                  data-testid="leave-end-date" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-medium">Reason</Label>
              <Textarea value={form.reason} onChange={e => setForm(p => ({ ...p, reason: e.target.value }))}
                className="rounded-sm" rows={3} data-testid="leave-reason-input" />
            </div>
            {user?.system_role !== 'super_admin' && (
              <label className={`flex items-start gap-2 p-3 border rounded-sm cursor-pointer transition-colors ${form.is_emergency ? 'border-red-300 bg-red-50/50' : 'border-neutral-200 hover:border-neutral-300'}`}>
                <input type="checkbox" checked={form.is_emergency}
                  onChange={e => setForm(p => ({ ...p, is_emergency: e.target.checked }))}
                  className="mt-0.5" data-testid="leave-emergency-checkbox" />
                <div>
                  <div className="flex items-center gap-1.5">
                    <AlertTriangle className="h-3.5 w-3.5 text-red-600" />
                    <span className="text-sm font-semibold text-red-700">This is an Emergency Leave</span>
                  </div>
                  <p className="text-[11px] text-neutral-600 mt-0.5">Goes directly to Super Admin for approval (bypasses Branch Manager).</p>
                </div>
              </label>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={handleApply} className="bg-red-600 hover:bg-red-700 text-white rounded-sm" data-testid="leave-submit-button">
              Submit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectDialog.open} onOpenChange={(o) => setRejectDialog({ open: o, id: rejectDialog.id })}>
        <DialogContent className="max-w-sm" data-testid="reject-leave-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold">Reject Leave</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <Label className="text-sm font-medium">Reason for rejection</Label>
            <Textarea value={rejectReason} onChange={e => setRejectReason(e.target.value)} rows={3} className="rounded-sm"
              data-testid="reject-reason-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectDialog({ open: false, id: null })} className="rounded-sm">Cancel</Button>
            <Button onClick={handleReject} className="bg-red-600 hover:bg-red-700 text-white rounded-sm" data-testid="reject-leave-confirm-button">
              Reject
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
